import type { JSX } from 'react';
import {
  readinessReportSchema,
  type BotScope,
} from '@cryptology-assist/domain';
import { botPath } from './api';
import { useRemote } from './hooks';
import { ErrorNotice } from './components';
import { dateLabel, type Copy, type Language } from './copy';

export function Readiness({
  scope,
  copy,
  language,
  onNavigate,
}: {
  scope: BotScope;
  copy: Copy;
  language: Language;
  onNavigate: (
    view:
      | 'configuration'
      | 'connections'
      | 'knowledge'
      | 'quality'
      | 'usage'
      | 'handoffOperations'
  ) => void;
}): JSX.Element {
  const report = useRemote(botPath(scope, 'readiness'), readinessReportSchema);
  const areas = {
    configuration: copy.configuration,
    subscription: copy.usage,
    sources: copy.source,
    email: copy.email,
    calendar: copy.calendar,
    quality: copy.quality,
    handoff: copy.handoffOperations,
  };
  const states = {
    passed: copy.diagnosticPassed,
    blocked: copy.diagnosticBlocked,
    review: copy.diagnosticReview,
    disabled: copy.diagnosticDisabled,
  };
  return (
    <section className="page-section">
      <header className="section-heading">
        <div>
          <h1>{copy.readiness}</h1>
          <p className="muted">{copy.readinessHelp}</p>
        </div>
        <button onClick={report.reload} disabled={report.loading}>
          {copy.runDiagnostics}
        </button>
      </header>
      <ErrorNotice error={report.error} copy={copy} onRetry={report.reload} />
      {report.loading && <p role="status">{copy.loading}</p>}
      {report.data && (
        <>
          <p className="muted">{dateLabel(report.data.checkedAt, language)}</p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{copy.diagnosticArea}</th>
                  <th>{copy.status}</th>
                  <th>{copy.diagnosticResult}</th>
                  <th>{copy.diagnosticNextStep}</th>
                </tr>
              </thead>
              <tbody>
                {report.data.checks.map((check, index) => {
                  const target =
                    check.area === 'handoff'
                      ? 'handoffOperations'
                      : check.area === 'configuration'
                        ? 'configuration'
                        : check.area === 'subscription'
                          ? 'usage'
                          : check.area === 'quality'
                            ? 'quality'
                            : check.area === 'sources' && !check.resource
                              ? 'knowledge'
                              : 'connections';
                  return (
                    <tr key={`${check.area}/${check.resource ?? index}`}>
                      <td>
                        {areas[check.area]}
                        {check.resource && (
                          <div className="small">{check.resource}</div>
                        )}
                      </td>
                      <td>
                        {check.code === 'SUBSCRIPTION_CAPACITY_LOW'
                          ? copy.diagnosticCapacityLow
                          : states[check.status]}
                      </td>
                      <td>
                        <code>{check.code}</code>
                      </td>
                      <td>
                        <button
                          className="quiet"
                          onClick={() => onNavigate(target)}
                        >
                          {copy[target]}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
